import { type MutableRefObject, type MouseEvent as ReactMouseEvent } from 'react';

import { type Row, type Table } from '@tanstack/react-table';

export const handleRowSelection = <T extends object>({
	row,
	table,
	lastSelectedRowIdRef,
	event,
}: {
	row: Row<T>;
	table: Table<T>;
	lastSelectedRowIdRef?: MutableRefObject<string | null>;
	event: ReactMouseEvent<Element, MouseEvent>;
}): void => {
	event.stopPropagation();

	const wasCurrentRowChecked = row.getIsSelected();

	// Toggle selection of this row
	row.toggleSelected();

	// If shift key is pressed and we have a last selected row, select range
	if (
		lastSelectedRowIdRef &&
		lastSelectedRowIdRef.current !== null &&
		(event.nativeEvent as MouseEvent).shiftKey
	) {
		const rows = table.getRowModel().rows;
		const lastIndex = rows.findIndex((r) => r.id === lastSelectedRowIdRef.current);
		const currentIndex = rows.findIndex((r) => r.id === row.id);

		if (lastIndex !== -1 && currentIndex !== -1) {
			const isLastIndexChecked = rows[lastIndex]?.getIsSelected() ?? false;

			const [start, end] =
				lastIndex < currentIndex ? [lastIndex, currentIndex] : [currentIndex, lastIndex];

			// Toggle selection of all rows between last selected and this one
			// but only if the last selected row is not the same as the current one
			if (wasCurrentRowChecked !== isLastIndexChecked) {
				for (let i = start; i <= end; i++) {
					if (rows[i]) {
						rows[i].toggleSelected(!wasCurrentRowChecked);
					}
				}
			}
		}
	}

	// Record the last selected row id
	if (lastSelectedRowIdRef) {
		lastSelectedRowIdRef.current = row.id;
	}
};
